// Layout-engine port of the Utility Bill generator's Xfinity-style page: the
// provider logo or name, the account block, the greeting, the "bill at a
// glance" box with its purple amount-due band, the grey "bill explained"
// panel, the help line and the detachable payment stub. Fixed positions,
// the way the generator draws it; the optional discount row shifts the
// "New charges" line under it. This module must not import layoutEngine.

const fmtBillDate = (s) => {
  if (!s) return "";
  const str = String(s);
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(str) ? `${str}T00:00:00` : str);
  return isNaN(d.getTime()) ? str : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
};
const currency = (n) => (parseFloat(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const flag = (v) => (v ? "true" : "");

const PURPLE = "#524fa2";   // rgb(0.322, 0.31, 0.635)
const DARK = "#333333";     // 0.2
const LIGHT = "#999999";    // 0.6
const PANEL = "#dcddde";    // rgb(0.863, 0.867, 0.871)
const GREEN = "#008000";
const T = (id, x, baseline, w, content, fontSize, extra = {}) =>
  ({ id, type: "text", x, y: +(baseline - 0.85 * fontSize).toFixed(2), w, content, fontSize, ...extra });
// one "bill at a glance" row: label at 60, value at 250, 18pt apart
const row = (id, baseline, label, value, extra = {}) => [
  T(`${id}-l`, 60, baseline, 185, label, 9, { color: DARK, ...extra }),
  T(`${id}-v`, 250, baseline, 90, value, 9, { color: DARK, ...extra }),
];

export const DEFAULT_UTILITY_BILL_LAYOUT = {
  page: { width: 612, height: 792 },
  meta: { baseDocType: "utility-bill" },
  elements: [
    // provider: the logo when there is one, else the name in purple
    { id: "ub-logo", type: "image", x: 50, y: 40, w: 120, h: 50, fit: "contain", src: "{logoDataUrl}", showIf: "hasLogo" },
    T("ub-company", 50, 70, 330, "{companyNameOrDefault}", 20, { bold: true, color: PURPLE, showIf: "!hasLogo" }),
    T("ub-acct-l", 400, 50, 130, "Account Number:", 9, { color: LIGHT }),
    T("ub-acct", 400, 62, 130, "{accountNumber}", 10, { bold: true, color: DARK }),
    T("ub-bdate-l", 400, 80, 130, "Billing Date:", 9, { color: LIGHT }),
    T("ub-bdate", 400, 92, 130, "{billingDate}", 10, { color: DARK }),
    T("ub-page", 540, 50, 60, "Page 1 of 1", 9, { color: LIGHT }),

    T("ub-hello", 50, 130, 500, "Hello {customerName},", 16, { bold: true, color: PURPLE }),
    T("ub-thanks", 50, 148, 500, "Thank you for choosing {companyNameOrUs}.", 10, { color: "#000000" }),

    // bill at a glance
    { id: "ub-box", type: "rect", x: 50, y: 178, w: 250, h: 180, fill: "none", stroke: PURPLE, lineWidth: 1 },
    T("ub-glance", 60, 198, 230, "Your bill at a glance", 14, { bold: true, color: PURPLE }),
    T("ub-for", 60, 213, 235, "For {serviceAddressLine}", 9, { color: "#000000" }),
    ...row("ub-r1", 243, "Previous balance", "{previousBalance}"),
    // a payment shows in green, no payment in the usual dark
    T("ub-r2-l", 60, 261, 185, "{paymentLabel}", 9, { color: DARK }),
    T("ub-r2-v", 250, 261, 90, "{paymentValue}", 9, { color: DARK, showIf: "!paymentIsCredit" }),
    T("ub-r2-vg", 250, 261, 90, "{paymentValue}", 9, { color: GREEN, showIf: "paymentIsCredit" }),
    ...row("ub-r3", 279, "Balance forward", "{balanceForward}", { bold: true }),
    ...row("ub-r4", 297, "Regular monthly charges", "{regularCharges}"),
    ...row("ub-r5", 315, "Taxes, fees and other charges", "{taxesAndFees}"),
    T("ub-r6-l", 60, 333, 185, "{discountLabel}", 9, { color: DARK, showIf: "hasDiscount" }),
    T("ub-r6-v", 250, 333, 90, "{discountValue}", 9, { color: GREEN, showIf: "hasDiscount" }),
    ...row("ub-r7", 333, "New charges", "{newCharges}", { bold: true, showIf: "!hasDiscount" }),
    ...row("ub-r7d", 351, "New charges", "{newCharges}", { bold: true, showIf: "hasDiscount" }),
    { id: "ub-due-band", type: "rect", x: 50, y: 358, w: 250, h: 35, fill: PURPLE, stroke: PURPLE, lineWidth: 1 },
    T("ub-due-l", 60, 378, 150, "Amount due", 11, { bold: true, color: "#ffffff" }),
    T("ub-due", 250, 378, 90, "{totalDue}", 11, { bold: true, color: "#ffffff" }),

    // bill explained
    { id: "ub-panel", type: "rect", x: 330, y: 178, w: 250, h: 180, fill: PANEL, stroke: "none" },
    T("ub-expl", 340, 198, 230, "Your bill explained", 14, { bold: true, color: DARK }),
    T("ub-expl-1", 340, 223, 235, "This page gives you a quick summary of your monthly", 7, { color: DARK }),
    T("ub-expl-2", 340, 237, 235, "bill. A detailed breakdown of your charges begins on page 3", 7, { color: DARK }),
    T("ub-usage-l", 340, 262, 230, "Usage this period:", 9, { bold: true, color: DARK, showIf: "hasUsage" }),
    T("ub-usage", 340, 276, 230, "{usageAmount} {usageUnit}", 9, { color: LIGHT, showIf: "hasUsage" }),
    T("ub-meter-l", 340, 301, 230, "Meter Information:", 9, { bold: true, color: DARK, showIf: "hasMeter" }),
    T("ub-meter", 340, 315, 230, "Meter #: {meterNumber}", 9, { color: LIGHT, showIf: "hasMeter" }),
    T("ub-reading", 340, 327, 230, "Reading: {previousReading} → {currentReading}", 9, { color: LIGHT, showIf: "hasReadings" }),

    // help
    { id: "ub-help-rule", type: "line", x: 50, y: 642, w: 512, h: 0, color: "#d9d9d9", lineWidth: 0.5 },
    T("ub-help", 50, 662, 200, "Need help?", 10, { bold: true, color: DARK }),
    T("ub-site", 50, 676, 190, "Visit {companyWebsite}", 9, { color: LIGHT, showIf: "hasCompanyWebsite" }),
    T("ub-phone", 250, 676, 190, "Call {companyPhone}", 9, { color: LIGHT, showIf: "hasCompanyPhone" }),

    // payment stub
    { id: "ub-stub-rule", type: "line", x: 50, y: 692, w: 512, h: 0, color: LIGHT, lineWidth: 1, dash: [5, 5] },
    T("ub-stub", 50, 712, 400, "PAYMENT STUB - Detach and return with payment", 8, { color: LIGHT }),
    T("ub-stub-acct", 50, 732, 190, "Account: {accountNumber}", 9, { color: DARK }),
    T("ub-stub-due", 250, 732, 160, "Amount Due: {totalDue}", 9, { bold: true, color: DARK }),
    T("ub-stub-date", 420, 732, 150, "Due Date: {dueDate}", 9, { color: DARK }),
  ],
};

export function buildUtilityBillContext(td) {
  const f = (td && td.formData) || {};
  const n = (v) => parseFloat(v) || 0;
  const previous = n(f.previousBalance), payment = n(f.paymentReceived);
  const base = n(f.baseCharge), usage = n(f.usageCharge), taxes = n(f.taxes), fees = n(f.fees), discount = n(f.discountAmount);
  const balanceForward = previous - payment;
  const currentCharges = base + usage + taxes + fees;
  const totalDue = balanceForward + currentCharges - discount;
  const logo = f.logoDataUrl || f.uploadedLogo || "";
  return {
    logoDataUrl: logo, hasLogo: flag(logo),
    companyName: f.companyName || "", companyNameOrDefault: f.companyName || "Utility Company", companyNameOrUs: f.companyName || "us",
    accountNumber: f.accountNumber || "-",
    billingDate: fmtBillDate(f.billingDate), dueDate: fmtBillDate(f.dueDate),
    customerName: f.customerName || "Customer",
    serviceAddressLine: `${f.serviceAddress || ""}, ${f.serviceCity || ""}, ${f.serviceState || ""} ${f.serviceZip || ""}`,
    previousBalance: currency(previous),
    paymentLabel: `Payment received${f.paymentDate ? ` - ${fmtBillDate(f.paymentDate)}` : ""}`,
    paymentValue: payment > 0 ? `-${currency(payment)}` : currency(0),
    paymentIsCredit: flag(payment > 0),
    balanceForward: currency(balanceForward),
    regularCharges: currency(base + usage),
    taxesAndFees: currency(taxes + fees),
    hasDiscount: flag(discount > 0), discountLabel: f.discountDescription || "Discount", discountValue: `-${currency(discount)}`,
    newCharges: currency(currentCharges - discount),
    totalDue: currency(totalDue),
    usageAmount: f.usageAmount || "", usageUnit: f.usageUnit || "", hasUsage: flag(f.usageAmount),
    meterNumber: f.meterNumber || "", hasMeter: flag(f.meterNumber),
    previousReading: f.previousReading || "", currentReading: f.currentReading || "",
    hasReadings: flag(f.meterNumber && f.previousReading && f.currentReading),
    companyWebsite: f.companyWebsite || "", hasCompanyWebsite: flag(f.companyWebsite),
    companyPhone: f.companyPhone || "", hasCompanyPhone: flag(f.companyPhone),
    serviceType: f.serviceType || "", accountStatus: f.accountStatus || "",
    servicePeriodStart: fmtBillDate(f.servicePeriodStart), servicePeriodEnd: fmtBillDate(f.servicePeriodEnd),
  };
}

export const UTILITY_BILL_SAMPLE = {
  formData: {
    providerId: "xfinity",
    companyName: "City Power & Light", companyAddress: "100 Utility Way",
    companyCity: "New York", companyState: "NY", companyZip: "10001", companyPhone: "(800) 555-0100",
    companyWebsite: "citypowerlight.com",
    customerName: "John Smith", accountNumber: "4477-8899-01",
    serviceAddress: "123 Main Street", serviceCity: "New York", serviceState: "NY", serviceZip: "10001",
    accountStatus: "Current", serviceType: "Electric",
    billingDate: "2026-09-01", servicePeriodStart: "2026-08-01", servicePeriodEnd: "2026-08-31", dueDate: "2026-09-20",
    previousBalance: "142.50", paymentReceived: "142.50", paymentDate: "2026-08-15",
    baseCharge: "24.00", usageCharge: "96.40", usageAmount: "812", usageUnit: "kWh",
    taxes: "9.85", fees: "4.50",
  },
};

export const UTILITY_BILL_TOKEN_GROUPS = [
  { group: "Provider", tokens: [
    ["{companyName}", "Company name"], ["{companyNameOrDefault}", "Company name (or Utility Company)"],
    ["{companyWebsite}", "Website"], ["{companyPhone}", "Phone"],
  ] },
  { group: "Account", tokens: [
    ["{customerName}", "Customer name"], ["{accountNumber}", "Account number"], ["{serviceAddressLine}", "Service address line"],
    ["{accountStatus}", "Account status"], ["{serviceType}", "Service type"],
    ["{billingDate}", "Billing date"], ["{dueDate}", "Due date"], ["{servicePeriodStart}", "Service period start"],
    ["{servicePeriodEnd}", "Service period end"],
  ] },
  { group: "Charges", tokens: [
    ["{previousBalance}", "Previous balance"], ["{paymentLabel}", "Payment received label"], ["{paymentValue}", "Payment received"],
    ["{balanceForward}", "Balance forward"], ["{regularCharges}", "Regular monthly charges"], ["{taxesAndFees}", "Taxes and fees"],
    ["{discountLabel}", "Discount label"], ["{discountValue}", "Discount"], ["{newCharges}", "New charges"], ["{totalDue}", "Amount due"],
    ["{usageAmount}", "Usage amount"], ["{usageUnit}", "Usage unit"],
  ] },
];

export const UTILITY_BILL_SHOW_IF = [
  ["", "Always"],
  ["hasLogo", "Has provider logo"],
  ["!hasLogo", "No provider logo"],
  ["paymentIsCredit", "A payment was received"],
  ["hasDiscount", "Has discount"],
  ["!hasDiscount", "No discount"],
  ["hasUsage", "Has usage amount"],
  ["hasMeter", "Has meter number"],
  ["hasCompanyWebsite", "Has website"],
  ["hasCompanyPhone", "Has phone"],
];

export const UTILITY_DOC_TYPES = {
  "utility-bill": {
    name: "Utility Bill", description: "Utility bill, Xfinity style", category: "utility-bill",
    layout: DEFAULT_UTILITY_BILL_LAYOUT, buildContext: buildUtilityBillContext,
    sample: UTILITY_BILL_SAMPLE, sampleLabel: "Sample bill",
    tokenGroups: UTILITY_BILL_TOKEN_GROUPS, showIf: UTILITY_BILL_SHOW_IF, tableBindings: [],
  },
};
